import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { lessonLogAPI, LessonLog, getYoutubeThumbnail } from '../../services/lessonLogAPI';
import toast from 'react-hot-toast';
import '../../styles/pages/teacher/_lesson-log.scss';

const LessonLogWrite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const studentId = searchParams.get('studentId');
  const date = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [student, setStudent] = useState<{ id: number; name: string; lessonType?: string } | null>(null);
  const [currentLog, setCurrentLog] = useState<LessonLog | null>(null);
  const [previousLog, setPreviousLog] = useState<LessonLog | null>(null);

  // 폼 데이터
  const [formData, setFormData] = useState({
    song: '',
    songYoutubeUrl: '',
    content: '',
    homework: '',
    teacherNote: ''
  });

  useEffect(() => {
    if (studentId) {
      loadData();
    }
  }, [studentId, date]);

  const loadData = async () => {
    if (!studentId) return;

    setLoading(true);
    try {
      const response = await lessonLogAPI.getByDate(parseInt(studentId), date);
      if (response.success) {
        const { current, previous, student: studentData } = response.data;
        setStudent(studentData);
        setPreviousLog(previous);
        setCurrentLog(current);

        if (current) {
          setFormData({
            song: current.song || '',
            songYoutubeUrl: current.songYoutubeUrl || '',
            content: current.content || '',
            homework: current.homework || '',
            teacherNote: current.teacherNote || ''
          });
        }
      }
    } catch (error) {
      toast.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (publish: boolean = false) => {
    if (!studentId) return;

    setSaving(true);
    try {
      const response = await lessonLogAPI.save({
        studentId: parseInt(studentId),
        lessonDate: date,
        ...formData,
        status: publish ? 'published' : 'draft',
        logId: currentLog?.id
      });

      if (response.success) {
        toast.success(publish ? '수업 기록이 발행되었습니다.' : '임시저장 되었습니다.');
        setCurrentLog(response.data);
      }
    } catch (error) {
      toast.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="lesson-log-page">
        <div className="page-loading">로딩 중...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="lesson-log-page">
        <div className="page-error">학생 정보를 찾을 수 없습니다.</div>
      </div>
    );
  }

  const previousThumbnail = previousLog?.songYoutubeUrl ? getYoutubeThumbnail(previousLog.songYoutubeUrl) : null;
  const currentThumbnail = formData.songYoutubeUrl ? getYoutubeThumbnail(formData.songYoutubeUrl) : null;

  return (
    <div className="lesson-log-page">
      <header className="log-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← 뒤로
        </button>
        <div className="header-info">
          <h1>{student.name} 수업 기록</h1>
          <span className="date">{format(new Date(date), 'yyyy년 M월 d일 (EEEE)', { locale: ko })}</span>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? '저장 중...' : '임시저장'}
          </button>
          <button className="btn-primary" onClick={() => handleSave(true)} disabled={saving}>
            {saving ? '저장 중...' : '저장 및 발행'}
          </button>
        </div>
      </header>

      <div className="log-content">
        {/* 이전 수업 (왼쪽) */}
        <div className="log-panel previous">
          <div className="panel-header">
            <h2>이전 수업</h2>
            {previousLog && (
              <span className="panel-date">
                {format(new Date(previousLog.lessonDate), 'M/d (E)', { locale: ko })}
              </span>
            )}
          </div>

          {previousLog ? (
            <div className="panel-body">
              <div className="log-field">
                <label>곡</label>
                <div className="field-value">
                  {previousLog.song || '-'}
                  {previousThumbnail && (
                    <a href={previousLog.songYoutubeUrl} target="_blank" rel="noopener noreferrer" className="youtube-preview">
                      <img src={previousThumbnail} alt="YouTube" />
                    </a>
                  )}
                </div>
              </div>

              <div className="log-field">
                <label>수업 내용</label>
                <div className="field-value pre">{previousLog.content || '-'}</div>
              </div>

              <div className="log-field">
                <label>과제</label>
                <div className="field-value pre">{previousLog.homework || '-'}</div>
              </div>

              <div className="log-field">
                <label>비고</label>
                <div className="field-value pre note">{previousLog.teacherNote || '-'}</div>
              </div>

              {previousLog.comments && previousLog.comments.length > 0 && (
                <div className="log-comments">
                  <label>댓글</label>
                  {previousLog.comments.map(comment => (
                    <div key={comment.id} className={`comment ${comment.authorType}`}>
                      <span className="author">
                        {comment.authorType === 'teacher' ? '선생님' : comment.student?.name}
                      </span>
                      <span className="message">{comment.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="panel-empty">이전 수업 기록이 없습니다.</div>
          )}
        </div>

        {/* 오늘 수업 (오른쪽) */}
        <div className="log-panel current">
          <div className="panel-header">
            <h2>오늘 수업</h2>
            {currentLog?.status === 'published' && (
              <span className="badge published">발행됨</span>
            )}
            {currentLog?.status === 'draft' && (
              <span className="badge draft">임시저장</span>
            )}
          </div>

          <div className="panel-body">
            <div className="log-field">
              <label>곡</label>
              <div className="input-with-button">
                <input
                  type="text"
                  value={formData.song}
                  onChange={(e) => handleChange('song', e.target.value)}
                  placeholder="곡 제목을 입력하세요"
                />
                <button
                  type="button"
                  className="btn-youtube-search"
                  onClick={() => {
                    if (formData.song.trim()) {
                      window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(formData.song + ' drum cover')}`, '_blank');
                    }
                  }}
                  disabled={!formData.song.trim()}
                  title="YouTube에서 검색"
                >
                  YouTube
                </button>
              </div>
            </div>

            <div className="log-field">
              <label>유튜브 링크</label>
              <input
                type="text"
                value={formData.songYoutubeUrl}
                onChange={(e) => handleChange('songYoutubeUrl', e.target.value)}
                placeholder="https://youtube.com/..."
              />
              {currentThumbnail && (
                <a href={formData.songYoutubeUrl} target="_blank" rel="noopener noreferrer" className="youtube-preview">
                  <img src={currentThumbnail} alt="YouTube" />
                </a>
              )}
            </div>

            <div className="log-field">
              <label>수업 내용</label>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="오늘 수업에서 배운 내용을 입력하세요"
                rows={4}
              />
            </div>

            <div className="log-field">
              <label>과제</label>
              <textarea
                value={formData.homework}
                onChange={(e) => handleChange('homework', e.target.value)}
                placeholder="과제를 입력하세요"
                rows={3}
              />
            </div>

            <div className="log-field">
              <label>비고 <span className="note-hint">(선생님만 볼 수 있음)</span></label>
              <textarea
                value={formData.teacherNote}
                onChange={(e) => handleChange('teacherNote', e.target.value)}
                placeholder="학생 상태, 진도, 특이사항 등"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonLogWrite;
